import moment from "moment";
import React, { useMemo } from "react";
import Linkify from "react-linkify";

import { ApiJobNote } from "@common/api/v1.1/types/jobs";
import { ApiUser } from "@common/api/v1.1/types/users";

type Props = Readonly<{
  canDelete: boolean;
  canEdit: boolean;
  onDelete: (noteId: string) => Promise<void>;
  onEdit: (noteId: string) => void;
  note: ApiJobNote;
  user: ApiUser;
}>;

const CONFIRM_DELETE = "Are you sure you want to delete this note?";

export const NoteContent = ({ canDelete, canEdit, onDelete, onEdit, note, user }: Props) => {
  const [displayContent, displayName, displayDateString] = useMemo(() => {
    let name = note.owner.name;
    if (user.id === note.owner.id) {
      name = "You";
    }
    let dateString = moment
      .unix(note.edited ? note.edited : note.created)
      .format("MMM Do YYYY \\a\\t hh:mm A");
    if (note.edited) {
      dateString = `Updated: ${dateString}`;
    }
    const split = note.content
      .split("\n")
      .map(s => s.trim())
      .filter(s => s.length > 0);    
    const scrollMatch = note.content.match(/\[page ([\d])+\]\(([\w\s\d-])+\)/);
    const content = (
      <Linkify
        componentDecorator={(href: string, text: string, key: number) => {
          return (
            <a key={key.toString()} href={href} target="_blank">
              {text}
            </a>
          );
        }}
      >
        {split.map((s, i) => (      
          <p key={i.toString()}>
            { scrollMatch && 
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  const pages = document.querySelector('#mappingPages');
                  const pagesStyle = pages?.getAttributeNode("style");
                  const elementPrefix = pagesStyle?.textContent?.match('left: 0px') ? '#' : '#support-';                  
                  const blockMatch = s.match(/\(([\w\s\d-])+\)/);
                  if (blockMatch) {
                    const block = blockMatch[0].toString().substr(1, blockMatch[0].toString().length - 2);
                    const element = document.querySelector(elementPrefix + block );
                    const elementClasses = element?.getAttribute("class") ?? "";
                    element?.scrollIntoView( { behavior: 'smooth', block: 'start' } );                    
                    element?.setAttribute("class", elementClasses + " note-scrolled"); 
                    setTimeout(() => {
                      element?.setAttribute("class", elementClasses + " fade-out"); 
                    }, 1000);
                    setTimeout(() => {
                      element?.setAttribute("class", elementClasses); 
                    }, 3000);
                  }
                }}
              >
                <i className="fa fa-arrow-left" />&nbsp;
              </a>
            }
            {s}
            </p>
        ))}        
      </Linkify>
    );
    return [content, name, dateString];
  }, [note, user]);
  return (
    <>
      <div className="note-header">
        <h5>{displayName} wrote:</h5>
        {canEdit && (
          <a
            className="hidden-action"
            onClick={e => {
              e.preventDefault();
              onEdit(note.id);
            }}
            href="#"
          >
            Edit
          </a>
        )}
        {canDelete && (
          <a
            className="hidden-action text-danger"
            onClick={e => {
              e.preventDefault();
              if (!window.confirm(CONFIRM_DELETE)) {
                return;
              }
              onDelete(note.id);
            }}
            href="#"
          >
            Delete
          </a>
        )}
      </div>
      <div className="note-content">{displayContent}</div>
      <small className="text-muted">{displayDateString}</small>
    </>
  );
};
NoteContent.displayName = "NoteContent";
